<script setup lang="ts">
import { isDefined } from "@voltalis/frontend-utils";
import { computed, ref, useTemplateRef, watch } from "vue";

import { TProgrammingDateRangeInputs } from "@/components/piloting/planning/programming-range/date-range-inputs/programming-date-range-inputs.types";
import { buildProgrammingRangeUpdateRequest } from "@/components/piloting/planning/programming-range/programming-range-form-dialog.utils";
import { DATA_TEST_IDS } from "@/constants/data-test-ids/data-test-ids.constant";
import { ProgramId, V4HSiteId } from "@/data/common/ids";
import {
  CreateProgrammingRangeBody,
  UpdateSiteSectionProgrammingRangeRequest,
} from "@/domains/v4b/site-programming-range/site-programming-range.types";
import { ProgrammingRange as PlanningProgrammingRange } from "@/domains/v4b/site-programming-range/site-programming-range.types";
import { useTypedIntl } from "@/locales/typed-intl";
import { EMixpanelEvent } from "@/services/mixpanel/mixpanel-events.type";
import { MixpanelService } from "@/services/mixpanel/mixpanel-service";

import DashboardDialog from "@/components/data/dashboard/dashboard-dialog.vue";
import ProgrammingDateRangeInputs from "@/components/piloting/planning/programming-range/date-range-inputs/programming-date-range-inputs.vue";
import SelectProgram from "@/components/piloting/planning/programming-range/select-program/select-program.vue";
import HospitalityButton from "@/components/ui/buttons/hospitality-button.vue";

const props = defineProps<{
  programmingRange?: PlanningProgrammingRange;
  selectedProgramCount: number;
  siteId: V4HSiteId;
  isLoading?: boolean;
}>();
const emits = defineEmits<{
  (e: "hide"): void;
  (e: "create-programming-range", payload: CreateProgrammingRangeBody): void;
  (e: "update-programming-range", payload: UpdateSiteSectionProgrammingRangeRequest): void;
}>();
const visible = defineModel<boolean>("visible", { required: true });

const { t, k } = useTypedIntl();

const dateRangeInputs = useTemplateRef("dateRangeInputs");

const selectedProgramId = ref<ProgramId | undefined>();

const isFormValid = computed<boolean>(() => {
  return (dateRangeInputs.value?.isValid() ?? false) && isDefined(selectedProgramId.value);
});

const isFormDirty = computed<boolean>(() => {
  const isProgramDirty = props.programmingRange?.program.id !== selectedProgramId.value;
  const isDateRangeDirty = !!dateRangeInputs.value?.isDirty();

  return isProgramDirty || isDateRangeDirty;
});

const isSubmitButtonEnabled = computed<boolean>(() => {
  return isFormValid.value && isFormDirty.value && !props.isLoading;
});

async function handleSubmitForm() {
  dateRangeInputs.value?.submit();
}

function resetForm() {
  selectedProgramId.value = undefined;
  dateRangeInputs.value?.resetForm();
}

async function handleCreateProgrammingRange(dateRange: TProgrammingDateRangeInputs) {
  if (!selectedProgramId.value) return;
  emits("create-programming-range", {
    programId: selectedProgramId.value,
    ...dateRange,
  });
}

function handleEditProgrammingRange(dateRange: Partial<TProgrammingDateRangeInputs>) {
  if (!isDefined(selectedProgramId.value) || !props.programmingRange) return;

  const request = buildProgrammingRangeUpdateRequest(props.programmingRange, selectedProgramId.value, dateRange);
  if (!request) return;

  emits("update-programming-range", request);
}

function handleHideDialog() {
  resetForm();
  emits("hide");
}

watch(
  () => props.programmingRange,
  (newValue) => {
    if (!newValue) return;
    selectedProgramId.value = newValue.program.id;
  },
  { immediate: true },
);

watch(visible, (newValue) => {
  if (!newValue || !props.programmingRange) return;

  MixpanelService.trackEvent(EMixpanelEvent.PROGRAMMING_RANGE_DETAILS_VIEWED);
});
</script>

<template>
  <DashboardDialog
    v-model:visible="visible"
    class="programming-range-form-dialog w-[616px]"
    :data-test-id="DATA_TEST_IDS.components.programmingRangeFormDialog.component"
    modal
    @hide="handleHideDialog"
  >
    <template #title>
      <h4 :data-test-id="DATA_TEST_IDS.components.programmingRangeFormDialog.title">
        <template v-if="props.programmingRange"> {{ t(k.control.piloting.planning.form.title.edit) }} </template>
        <template v-else> {{ t(k.control.piloting.planning.form.title.create) }} </template>
      </h4>
    </template>

    <template #subtitle>
      <span
        v-if="!props.programmingRange"
        class="text-text-secondary"
        :data-test-id="DATA_TEST_IDS.components.programmingRangeFormDialog.subtitle"
      >
        {{ t(k.control.piloting.planning.form.countSelectedSections({ count: props.selectedProgramCount })) }}
      </span>
    </template>

    <div class="flex flex-col gap-4">
      <section class="flex flex-col gap-3">
        <small class="uppercase text-text-secondary font-medium">{{ t(k.global.misc.program) }}</small>
        <div class="flex flex-col max-h-[220px] overflow-y-auto w-full gap-2">
          <SelectProgram
            v-model:selected-program-id="selectedProgramId"
            :disabled="props.isLoading"
            :site-id="props.siteId"
          />
        </div>
      </section>

      <section class="flex flex-col gap-3">
        <small class="uppercase text-text-secondary font-medium">{{ t(k.global.misc.duration) }}</small>
        <ProgrammingDateRangeInputs
          ref="dateRangeInputs"
          :disabled="props.isLoading"
          :initial-date-range="props.programmingRange"
          @submit-create="handleCreateProgrammingRange"
          @submit-edit="handleEditProgrammingRange"
        />
      </section>
    </div>

    <template #footer="{ closeCallback }">
      <div class="flex w-full gap-3">
        <HospitalityButton
          class="flex flex-1"
          :data-test-id="DATA_TEST_IDS.components.programmingRangeFormDialog.cancelButton"
          :label="t(k.global.misc.cancel)"
          severity="secondary"
          @click="closeCallback"
        />
        <HospitalityButton
          class="flex flex-1"
          :data-test-id="DATA_TEST_IDS.components.programmingRangeFormDialog.submitButton"
          :disabled="!isSubmitButtonEnabled"
          :label="t(k.global.misc.apply)"
          @click="handleSubmitForm"
        />
      </div>
    </template>
  </DashboardDialog>
</template>
